import { ARENA_BOUNDS, BALL_RADIUS, PHYSICS } from "./config";
import type { WallColor } from "./config";
import {
  getAuthoredLevelIds,
  getLevelDefinition,
  type LevelId,
  type LevelBallDefinition,
  type LevelDefinition,
  type LevelReference,
} from "./levels";

type Charge = LevelBallDefinition["charge"];

type ComplexityTier = "simple" | "light" | "moderate" | "complex" | "severe";

interface ProbeBallState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  charge: Charge;
}

interface ProbePhysics {
  fixedStepMs: number;
  forceStrength: number;
  softening: number;
  damping: number;
  bounce: number;
  maxSpeed: number;
}

interface ProbeMetrics {
  probeDurationMs: number;
  collisionTimeMs: number | null;
  survivedProbe: boolean;
  minDistanceRatio: number;
  closeCallCount: number;
  wallContacts: number;
  chargeChangingWallContacts: number;
  averageObservedSpeed: number;
  peakObservedSpeed: number;
}

export interface LevelComplexityMetrics {
  ballCount: number;
  nonGrayWallCount: number;
  distinctWallColorCount: number;
  durationSeconds: number;
  averageStartSpeed: number;
  peakStartSpeed: number;
  averageNearestGap: number;
  minimumNearestGap: number;
  chargeBalanceRatio: number;
  adjacentChargeTransitionRatio: number;
  openingProbe: ProbeMetrics;
}

export interface LevelComplexityBreakdown {
  ballCount: number;
  wallComplexity: number;
  durationPressure: number;
  startSpeed: number;
  crowding: number;
  chargePattern: number;
  openingChaos: number;
  openingRisk: number;
}

export interface LevelComplexityReport {
  level: LevelId;
  totalScore: number;
  tier: ComplexityTier;
  rank: number | null;
  deltaFromPrevious: number | null;
  dominantFactors: string[];
  warnings: string[];
  metrics: LevelComplexityMetrics;
  breakdown: LevelComplexityBreakdown;
}

export interface PolarityComplexityApi {
  getLevel(level: LevelReference): LevelComplexityReport;
  getAuthoredLevels(): LevelComplexityReport[];
  getRankedAuthoredLevels(): LevelComplexityReport[];
}

const COMPLEXITY_PROBE_DURATION_MS = 6_000;
const CLOSE_CALL_RATIO = 1.15;

export function createPolarityComplexityApi(): PolarityComplexityApi {
  return {
    getLevel(level: LevelReference): LevelComplexityReport {
      return calculateLevelComplexity(getLevelDefinition(level));
    },
    getAuthoredLevels(): LevelComplexityReport[] {
      const authoredLevels = getAuthoredLevelIds();
      const ranked = rankComplexityReports(
        authoredLevels.map((level) =>
          calculateLevelComplexity(getLevelDefinition(level)),
        ),
      );
      const rankedByLevel = new Map(ranked.map((report) => [report.level, report]));

      return authoredLevels
        .map((level) => rankedByLevel.get(level))
        .filter((report): report is LevelComplexityReport => report !== undefined);
    },
    getRankedAuthoredLevels(): LevelComplexityReport[] {
      return rankComplexityReports(
        getAuthoredLevelIds().map((level) =>
          calculateLevelComplexity(getLevelDefinition(level)),
        ),
      );
    },
  };
}

export function calculateLevelComplexity(level: LevelDefinition): LevelComplexityReport {
  const wallColors = [
    level.walls.left,
    level.walls.right,
    level.walls.top,
    level.walls.bottom,
  ];
  const nonGrayWallCount = wallColors.filter((color) => color !== "gray").length;
  const distinctWallColorCount = new Set(wallColors).size;
  const durationSeconds = level.durationMs / 1000;
  const startSpeeds = level.balls.map((ball) => Math.hypot(ball.vx, ball.vy));
  const averageStartSpeed = average(startSpeeds);
  const peakStartSpeed = startSpeeds.length > 0 ? Math.max(...startSpeeds) : 0;
  const spacing = computeSpacingMetrics(level);
  const chargePattern = computeChargePatternMetrics(level);
  const openingProbe = runOpeningProbe(level);
  const volatilityMultiplier = computeVolatilityMultiplier(
    level.balls.length,
    averageStartSpeed,
    spacing.minimumNearestGap,
  );

  const breakdown: LevelComplexityBreakdown = {
    ballCount: roundToTenths(Math.max(0, 4 + (level.balls.length - 3) * 6)),
    wallComplexity: roundToTenths(
      nonGrayWallCount * 0.6 + Math.max(0, distinctWallColorCount - 1) * 0.4,
    ),
    durationPressure: roundToTenths(durationSeconds * 0.05),
    startSpeed: roundToTenths(
      Math.max(0, (averageStartSpeed - 24) * 0.16) +
        Math.max(0, (peakStartSpeed - 32) * 0.04),
    ),
    crowding: roundToTenths(
      Math.max(0, (230 - spacing.averageNearestGap) * 0.04) +
        Math.max(0, (180 - spacing.minimumNearestGap) * 0.05),
    ),
    chargePattern: roundToTenths(
      chargePattern.chargeBalanceRatio * 1.5 +
        chargePattern.adjacentChargeTransitionRatio * 1.5,
    ),
    openingChaos: roundToTenths(
      (
        Math.max(0, (openingProbe.averageObservedSpeed - 35) * 0.08) +
        Math.max(0, (openingProbe.peakObservedSpeed - 90) * 0.02) +
        openingProbe.wallContacts * 0.15 +
        openingProbe.chargeChangingWallContacts * 0.4
      ) * volatilityMultiplier,
    ),
    openingRisk: roundToTenths(
      scoreOpeningRisk(openingProbe) * volatilityMultiplier,
    ),
  };

  const totalScore = roundToTenths(
    Object.values(breakdown).reduce((sum, value) => sum + value, 0),
  );

  const metrics: LevelComplexityMetrics = {
    ballCount: level.balls.length,
    nonGrayWallCount,
    distinctWallColorCount,
    durationSeconds: roundToTenths(durationSeconds),
    averageStartSpeed: roundToTenths(averageStartSpeed),
    peakStartSpeed: roundToTenths(peakStartSpeed),
    averageNearestGap: roundToTenths(spacing.averageNearestGap),
    minimumNearestGap: roundToTenths(spacing.minimumNearestGap),
    chargeBalanceRatio: roundToTenths(chargePattern.chargeBalanceRatio),
    adjacentChargeTransitionRatio: roundToTenths(
      chargePattern.adjacentChargeTransitionRatio,
    ),
    openingProbe: {
      probeDurationMs: openingProbe.probeDurationMs,
      collisionTimeMs: openingProbe.collisionTimeMs,
      survivedProbe: openingProbe.survivedProbe,
      minDistanceRatio: roundToTenths(openingProbe.minDistanceRatio),
      closeCallCount: openingProbe.closeCallCount,
      wallContacts: openingProbe.wallContacts,
      chargeChangingWallContacts: openingProbe.chargeChangingWallContacts,
      averageObservedSpeed: roundToTenths(openingProbe.averageObservedSpeed),
      peakObservedSpeed: roundToTenths(openingProbe.peakObservedSpeed),
    },
  };

  return {
    level: level.level,
    totalScore,
    tier: scoreToTier(totalScore),
    rank: null,
    deltaFromPrevious: null,
    dominantFactors: getDominantFactors(breakdown),
    warnings: buildWarnings(metrics),
    metrics,
    breakdown,
  };
}

function rankComplexityReports(reports: LevelComplexityReport[]): LevelComplexityReport[] {
  const ranked = [...reports].sort((left, right) => left.totalScore - right.totalScore);
  let previousScore: number | null = null;

  return ranked.map((report, index) => {
    const deltaFromPrevious =
      previousScore === null
        ? null
        : roundToTenths(report.totalScore - previousScore);
    previousScore = report.totalScore;

    return {
      ...report,
      rank: index + 1,
      deltaFromPrevious,
    };
  });
}

function computeSpacingMetrics(level: LevelDefinition): {
  averageNearestGap: number;
  minimumNearestGap: number;
} {
  if (level.balls.length <= 1) {
    return { averageNearestGap: 999, minimumNearestGap: 999 };
  }

  const nearestGaps = level.balls.map((ball, index) => {
    let nearestGap = Number.POSITIVE_INFINITY;
    for (let otherIndex = 0; otherIndex < level.balls.length; otherIndex += 1) {
      if (index === otherIndex) {
        continue;
      }
      const otherBall = level.balls[otherIndex];
      const centerDistance = Math.hypot(otherBall.x - ball.x, otherBall.y - ball.y);
      nearestGap = Math.min(nearestGap, centerDistance - BALL_RADIUS * 2);
    }
    return nearestGap;
  });

  return {
    averageNearestGap: average(nearestGaps),
    minimumNearestGap: Math.min(...nearestGaps),
  };
}

function computeChargePatternMetrics(level: LevelDefinition): {
  chargeBalanceRatio: number;
  adjacentChargeTransitionRatio: number;
} {
  const positiveCount = level.balls.filter((ball) => ball.charge > 0).length;
  const negativeCount = level.balls.length - positiveCount;
  const chargeBalanceRatio =
    level.balls.length === 0
      ? 0
      : 1 - Math.abs(positiveCount - negativeCount) / level.balls.length;

  if (level.balls.length <= 1) {
    return {
      chargeBalanceRatio,
      adjacentChargeTransitionRatio: 0,
    };
  }

  const centerX = ARENA_BOUNDS.x + ARENA_BOUNDS.width / 2;
  const centerY = ARENA_BOUNDS.y + ARENA_BOUNDS.height / 2;
  const ordered = [...level.balls].sort((left, right) => {
    const leftAngle = Math.atan2(left.y - centerY, left.x - centerX);
    const rightAngle = Math.atan2(right.y - centerY, right.x - centerX);
    return leftAngle - rightAngle;
  });

  let transitions = 0;
  for (let index = 0; index < ordered.length; index += 1) {
    const current = ordered[index];
    const next = ordered[(index + 1) % ordered.length];
    if (current.charge !== next.charge) {
      transitions += 1;
    }
  }

  return {
    chargeBalanceRatio,
    adjacentChargeTransitionRatio: transitions / ordered.length,
  };
}

function runOpeningProbe(level: LevelDefinition): ProbeMetrics {
  const physics = buildProbePhysics(level);
  const balls: ProbeBallState[] = level.balls.map((ball, index) => ({
    id: index + 1,
    x: ball.x,
    y: ball.y,
    vx: ball.vx,
    vy: ball.vy,
    radius: BALL_RADIUS,
    charge: ball.charge,
  }));

  const probeSteps = Math.max(
    1,
    Math.round(COMPLEXITY_PROBE_DURATION_MS / physics.fixedStepMs),
  );
  let minDistanceRatio = Number.POSITIVE_INFINITY;
  let closeCallCount = 0;
  let wallContacts = 0;
  let chargeChangingWallContacts = 0;
  let speedSampleCount = 0;
  let speedAccumulator = 0;
  let peakObservedSpeed = 0;
  let collisionTimeMs: number | null = null;

  for (let stepIndex = 0; stepIndex < probeSteps; stepIndex += 1) {
    const dt = physics.fixedStepMs / 1000;
    applyForcesToBalls(balls, physics, dt);
    const wallMetrics = integratePositionsForProbe(balls, level.walls, physics, dt);
    wallContacts += wallMetrics.wallContacts;
    chargeChangingWallContacts += wallMetrics.chargeChangingWallContacts;

    const proximity = measureProximity(balls);
    minDistanceRatio = Math.min(minDistanceRatio, proximity.minDistanceRatio);
    closeCallCount += proximity.closeCallCount;

    for (const ball of balls) {
      const speed = Math.hypot(ball.vx, ball.vy);
      speedAccumulator += speed;
      speedSampleCount += 1;
      peakObservedSpeed = Math.max(peakObservedSpeed, speed);
    }

    if (proximity.collisionDetected) {
      collisionTimeMs = roundToTenths((stepIndex + 1) * physics.fixedStepMs);
      break;
    }
  }

  return {
    probeDurationMs:
      collisionTimeMs ?? roundToTenths(probeSteps * physics.fixedStepMs),
    collisionTimeMs,
    survivedProbe: collisionTimeMs === null,
    minDistanceRatio:
      minDistanceRatio === Number.POSITIVE_INFINITY ? 99 : minDistanceRatio,
    closeCallCount,
    wallContacts,
    chargeChangingWallContacts,
    averageObservedSpeed:
      speedSampleCount > 0 ? speedAccumulator / speedSampleCount : 0,
    peakObservedSpeed,
  };
}

function buildProbePhysics(level: LevelDefinition): ProbePhysics {
  return {
    fixedStepMs: PHYSICS.fixedStepMs,
    forceStrength: level.forceStrength,
    softening: PHYSICS.softening,
    damping: PHYSICS.damping,
    bounce: level.bounce,
    maxSpeed: PHYSICS.maxSpeed,
  };
}

function applyForcesToBalls(
  balls: ProbeBallState[],
  physics: ProbePhysics,
  dt: number,
): void {
  const deltaVx = new Array(balls.length).fill(0);
  const deltaVy = new Array(balls.length).fill(0);
  const softeningSq = physics.softening * physics.softening;

  for (let i = 0; i < balls.length; i += 1) {
    const a = balls[i];
    for (let j = i + 1; j < balls.length; j += 1) {
      const b = balls[j];
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      let distanceSq = dx * dx + dy * dy;

      if (distanceSq < 0.0001) {
        dx = (j - i) * 0.01;
        dy = 0.01;
        distanceSq = dx * dx + dy * dy;
      }

      const distance = Math.sqrt(distanceSq);
      const nx = dx / distance;
      const ny = dy / distance;
      const direction = a.charge === b.charge ? -1 : 1;
      const forceMagnitude = physics.forceStrength / (distanceSq + softeningSq);
      const forceX = nx * forceMagnitude * direction;
      const forceY = ny * forceMagnitude * direction;

      deltaVx[i] += forceX * dt;
      deltaVy[i] += forceY * dt;
      deltaVx[j] -= forceX * dt;
      deltaVy[j] -= forceY * dt;
    }
  }

  const dampingFactor = Math.exp(-physics.damping * dt);
  for (let index = 0; index < balls.length; index += 1) {
    const ball = balls[index];
    ball.vx = (ball.vx + deltaVx[index]) * dampingFactor;
    ball.vy = (ball.vy + deltaVy[index]) * dampingFactor;

    const speed = Math.hypot(ball.vx, ball.vy);
    if (speed > physics.maxSpeed) {
      const scale = physics.maxSpeed / speed;
      ball.vx *= scale;
      ball.vy *= scale;
    }
  }
}

function integratePositionsForProbe(
  balls: ProbeBallState[],
  walls: LevelDefinition["walls"],
  physics: ProbePhysics,
  dt: number,
): {
  wallContacts: number;
  chargeChangingWallContacts: number;
} {
  const minX = ARENA_BOUNDS.x + BALL_RADIUS;
  const maxX = ARENA_BOUNDS.x + ARENA_BOUNDS.width - BALL_RADIUS;
  const minY = ARENA_BOUNDS.y + BALL_RADIUS;
  const maxY = ARENA_BOUNDS.y + ARENA_BOUNDS.height - BALL_RADIUS;
  let wallContacts = 0;
  let chargeChangingWallContacts = 0;

  for (const ball of balls) {
    ball.x += ball.vx * dt;
    ball.y += ball.vy * dt;

    if (ball.x < minX) {
      ball.x = minX;
      ball.vx = Math.abs(ball.vx) * physics.bounce;
      const changedCharge = applyWallColor(ball, walls.left);
      wallContacts += 1;
      chargeChangingWallContacts += changedCharge ? 1 : 0;
    } else if (ball.x > maxX) {
      ball.x = maxX;
      ball.vx = -Math.abs(ball.vx) * physics.bounce;
      const changedCharge = applyWallColor(ball, walls.right);
      wallContacts += 1;
      chargeChangingWallContacts += changedCharge ? 1 : 0;
    }

    if (ball.y < minY) {
      ball.y = minY;
      ball.vy = Math.abs(ball.vy) * physics.bounce;
      const changedCharge = applyWallColor(ball, walls.top);
      wallContacts += 1;
      chargeChangingWallContacts += changedCharge ? 1 : 0;
    } else if (ball.y > maxY) {
      ball.y = maxY;
      ball.vy = -Math.abs(ball.vy) * physics.bounce;
      const changedCharge = applyWallColor(ball, walls.bottom);
      wallContacts += 1;
      chargeChangingWallContacts += changedCharge ? 1 : 0;
    }
  }

  return { wallContacts, chargeChangingWallContacts };
}

function applyWallColor(ball: ProbeBallState, wallColor: WallColor): boolean {
  const wallCharge = wallColorToCharge(wallColor);
  if (wallCharge === null) {
    return false;
  }

  const changedCharge = ball.charge !== wallCharge;
  ball.charge = wallCharge;
  return changedCharge;
}

function measureProximity(balls: ProbeBallState[]): {
  minDistanceRatio: number;
  closeCallCount: number;
  collisionDetected: boolean;
} {
  if (balls.length <= 1) {
    return {
      minDistanceRatio: 99,
      closeCallCount: 0,
      collisionDetected: false,
    };
  }

  let minDistanceRatio = Number.POSITIVE_INFINITY;
  let closeCallCount = 0;
  let collisionDetected = false;

  for (let i = 0; i < balls.length; i += 1) {
    const a = balls[i];
    for (let j = i + 1; j < balls.length; j += 1) {
      const b = balls[j];
      const threshold = a.radius + b.radius;
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      const ratio = distance / threshold;
      minDistanceRatio = Math.min(minDistanceRatio, ratio);

      if (ratio <= CLOSE_CALL_RATIO) {
        closeCallCount += 1;
      }
      if (ratio <= 1) {
        collisionDetected = true;
      }
    }
  }

  return {
    minDistanceRatio,
    closeCallCount,
    collisionDetected,
  };
}

function scoreOpeningRisk(probe: ProbeMetrics): number {
  if (probe.collisionTimeMs !== null) {
    return Math.max(0, (3_500 - probe.collisionTimeMs) / 450);
  }

  return (
    Math.max(0, (1.18 - probe.minDistanceRatio) * 9) +
    Math.min(4, probe.closeCallCount * 0.18)
  );
}

function scoreToTier(score: number): ComplexityTier {
  if (score < 12) {
    return "simple";
  }
  if (score < 24) {
    return "light";
  }
  if (score < 36) {
    return "moderate";
  }
  if (score < 48) {
    return "complex";
  }
  return "severe";
}

function computeVolatilityMultiplier(
  ballCount: number,
  averageStartSpeed: number,
  minimumNearestGap: number,
): number {
  return Math.max(
    0.35,
    0.35 +
      Math.max(0, ballCount - 3) * 0.18 +
      Math.max(0, averageStartSpeed - 20) * 0.015 +
      Math.max(0, 170 - minimumNearestGap) * 0.004,
  );
}

function getDominantFactors(
  breakdown: LevelComplexityBreakdown,
): string[] {
  return Object.entries(breakdown)
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map(([name]) => name);
}

function buildWarnings(metrics: LevelComplexityMetrics): string[] {
  const warnings: string[] = [];

  if (metrics.openingProbe.collisionTimeMs !== null) {
    warnings.push(
      `No-input opening collides after ${roundToTenths(
        metrics.openingProbe.collisionTimeMs / 1000,
      )}s.`,
    );
  }

  if (metrics.minimumNearestGap < 18) {
    warnings.push("Starting spacing is tight enough to create crowded openings.");
  }

  if (metrics.nonGrayWallCount >= 3) {
    warnings.push("Most walls recolor balls, which can sharply increase state volatility.");
  }

  if (metrics.averageStartSpeed > 34 && metrics.ballCount >= 5) {
    warnings.push("High starting speed is stacked on top of a larger ball count.");
  }

  if (metrics.openingProbe.chargeChangingWallContacts >= 5) {
    warnings.push("Opening probe shows frequent wall-driven recoloring.");
  }

  return warnings;
}

function wallColorToCharge(color: WallColor): Charge | null {
  switch (color) {
    case "red":
      return 1;
    case "blue":
      return -1;
    case "gray":
      return null;
  }
}

function average(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function roundToTenths(value: number): number {
  return Math.round(value * 10) / 10;
}
